if (!customElements.get('pack-selector')) {
  const PACK_OFFER_PREFIX = '[IG] Bundle';
  // Past this the page gives up on Intelligems and shows the plain quantity dropdown.
  const IG_TIMEOUT_MS = 6000;

  // Outlives the product-info swap on a color change, so the chosen pack carries over.
  const packMemory = { line: null, q: 1 };

  const money = (cents) => '$' + (cents % 100 === 0 ? cents / 100 : (cents / 100).toFixed(2));

  // igData.campaigns is briefly missing while igData.reset() re-runs Intelligems; `ig:ready` fires again after.
  function whenIgReady(callback) {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      callback(window.igData?.campaigns ? window.igData : null);
    };
    if (window.igData?.campaigns) return finish();
    window.addEventListener('ig:ready', finish, { once: true });
    setTimeout(finish, IG_TIMEOUT_MS);
  }

  /* ---------- offers ---------- */

  function isPackOffer(campaign) {
    const d = campaign.discount;
    return (
      campaign.name.startsWith(PACK_OFFER_PREFIX) &&
      d?.discountApplicationType === 'tieredDiscount' &&
      d.discountApplicationMethod === 'perItem' &&
      d.discountType === 'dollar' &&
      Array.isArray(d.offerProducts) &&
      d.offerProducts.length > 0
    );
  }

  const coveredVariantIds = (campaign) =>
    new Set(
      campaign.discount.offerProducts.flatMap((p) =>
        p.variants.filter((v) => v.inclusionType === 'include').map((v) => String(v.id)),
      ),
    );

  // Cents off per unit at quantity q; a tier's maximum of 1e9 means "and up".
  function unitOffAt(campaign, q) {
    const tier = campaign.discount.tiers.find((t) => q >= t.minimumUnits && q <= t.maximumUnits);
    return tier ? Math.round(tier.unitDiscount * 100) : 0;
  }

  function packSizes(campaign) {
    const largest = Math.max(...campaign.discount.tiers.map((t) => t.minimumUnits));
    const sizes = [];
    for (let q = 2; q <= largest; q++) if (unitOffAt(campaign, q) > 0) sizes.push(q);
    return sizes;
  }

  // Best per-unit rate at 2, then 3, then 4….
  function compareOffers(a, b) {
    const largest = Math.max(...packSizes(a), ...packSizes(b), 2);
    for (let q = 2; q <= largest; q++) {
      const diff = unitOffAt(b, q) - unitOffAt(a, q);
      if (diff) return diff;
    }
    return 0;
  }

  // An offer the visitor already holds wins: unassigning only takes effect on the next page
  // load, so switching would stack both discounts in the cart until then.
  function choosePackOffer(ig, variantId) {
    const candidates = ig.campaigns
      .getAll({ includeUnassigned: true })
      .filter((c) => isPackOffer(c) && coveredVariantIds(c).has(String(variantId)));
    const held = candidates.filter((c) => c.assigned);
    return (held.length ? held : candidates).sort(compareOffers)[0] || null;
  }

  /* ---------- gallery preview ---------- */

  // Safari has no scroll anchoring, so a gallery swap slides the sticky buy box; while it settles,
  // scroll back by however far the anchor drifted without user input.
  // Returns a function that stops the hold early.
  function holdViewportAnchor(anchorEl, durationMs) {
    let userMoved = false;
    const markUser = () => {
      userMoved = true;
    };
    const opts = { passive: true, capture: true };
    const inputEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    inputEvents.forEach((t) => window.addEventListener(t, markUser, opts));
    let anchorTop = anchorEl.getBoundingClientRect().top;
    const startedAt = performance.now();
    const tick = () => {
      if (userMoved || performance.now() - startedAt > durationMs) {
        inputEvents.forEach((t) => window.removeEventListener(t, markUser, opts));
        return;
      }
      if (document.contains(anchorEl)) {
        const delta = anchorEl.getBoundingClientRect().top - anchorTop;
        if (Math.abs(delta) > 1) window.scrollBy(0, delta);
        anchorTop = anchorEl.getBoundingClientRect().top;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => {
      userMoved = true;
    };
  }

  // Holds the old gallery's height until the new images load, so the page does not jump.
  function replaceGalleryPreservingScroll(oldGallery, newGallery) {
    const x = window.scrollX;
    const y = window.scrollY;
    const oldHeight = oldGallery.getBoundingClientRect().height;
    if (oldHeight > 0) newGallery.style.minHeight = oldHeight + 'px';
    oldGallery.replaceWith(newGallery);
    window.scrollTo(x, y);
    if (oldHeight <= 0) return;
    const pending = [...newGallery.querySelectorAll('img')]
      .filter((img) => !img.complete)
      .map((img) => new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      }));
    Promise.race([Promise.all(pending), new Promise((resolve) => setTimeout(resolve, 2500))]).then(() => {
      newGallery.style.minHeight = '';
    });
  }

  // Swaps in another color's gallery, lightbox and zoom, as the color swatches above do.
  function swapGallery(productInfo, doc) {
    const oldGallery = productInfo.querySelector('.product__media-wrapper');
    const newGallery = doc.querySelector('.product__media-wrapper');
    if (!oldGallery || !newGallery) return;
    replaceGalleryPreservingScroll(oldGallery, newGallery.cloneNode(true));
    const oldModal = document.querySelector('product-modal .product-media-modal__content');
    const newModal = doc.querySelector('product-modal .product-media-modal__content');
    if (oldModal && newModal) oldModal.innerHTML = newModal.innerHTML;
    if (typeof enableZoomOnHover === 'function' && productInfo.hasAttribute?.('data-zoom-on-hover')) {
      enableZoomOnHover(2);
    }
  }

  /* ---------- element ---------- */

  class PackSelector extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.line = this.dataset.line;
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.price = Number(this.dataset.price);
      this.picks = new Map();
      this.customizing = false;
      this.q = 1;
      this.galleryProductId = this.productId;
      this.gallerySections = new Map();

      this.productInfo = this.closest('product-info') || document;
      this.cta = this.productInfo.querySelector('.quantity-buy-btn-container--pack .custom-swatches-btn-mobile');
      this.grid = this.querySelector('.pack-selector__grid');
      this.link = this.querySelector('.pack-selector__customize');
      this.builder = this.querySelector('.pack-selector__builder');
      this.swatchList = this.querySelector('.pack-selector__swatches');
      this.errorEl = this.querySelector('.pack-selector__error');
      this.readColors();

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
      this.onAdd = this.onAdd.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('change', this.onInputChange);
      this.cta?.addEventListener('click', this.onAdd);
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
      }

      this.setState('pending');
      whenIgReady((ig) => this.resolve(ig));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('change', this.onInputChange);
      this.cta?.removeEventListener('click', this.onAdd);
      this.unsubscribe?.();
    }

    // Colors of the line in the selected size; re-rendered by product-info on a size change.
    readColors() {
      const colors = JSON.parse(this.querySelector(`#pack-colors-${this.sectionId}`).textContent);
      this.colors = new Map(colors.map((c) => [String(c.productId), c]));
    }

    setState(state) {
      this.state = state;
      this.dataset.state = state;
    }

    resolve(ig) {
      const current = this.colors.get(this.productId);
      this.offer = ig && current && !current.sale ? choosePackOffer(ig, this.variantId) : null;
      if (!this.offer) {
        this.picks.clear();
        this.setState('none');
        return;
      }
      this.coveredVariants = coveredVariantIds(this.offer);
      this.sizes = packSizes(this.offer);
      this.renderTiles();
      this.renderSwatches();
      this.setState('ready');
      const remembered = packMemory.line === this.line ? packMemory.q : 1;
      this.select(remembered === 1 || this.sizes.includes(remembered) ? remembered : 1);
    }

    // Line colors the shopper can put in a pack: covered by the offer, not on sale, in stock.
    eligible(color) {
      return color && !color.sale && color.available && this.coveredVariants.has(String(color.variantId));
    }

    color(productId) {
      const c = this.colors.get(String(productId));
      return this.eligible(c) ? c : null;
    }

    /* ---------- state ---------- */

    select(q, focus = false) {
      this.q = q;
      packMemory.line = this.line;
      packMemory.q = q;
      this.hideError();

      this.grid.querySelectorAll('.pack-selector__tier').forEach((tier) => {
        const on = Number(tier.dataset.q) === q;
        tier.classList.toggle('is-selected', on);
        tier.setAttribute('aria-checked', on);
        tier.setAttribute('tabindex', on ? '0' : '-1');
        if (on && focus) tier.focus();
      });

      if (q === 1) {
        this.customizing = false;
        this.picks.clear();
        this.previewGallery(this.productId);
      } else {
        this.assignOffer();
        if (this.customizing) this.trimTo(q);
        else this.seed();
      }
      this.render();
    }

    seed() {
      this.picks.clear();
      const current = this.color(this.productId);
      this.setPick(this.productId, current ? Math.min(this.q, current.stock) : 0);
      // Not enough of this color in this size: let the shopper finish the pack with others.
      this.customizing = this.total() < this.q;
    }

    setPick(productId, qty) {
      if (qty > 0) this.picks.set(String(productId), qty);
      else this.picks.delete(String(productId));
    }

    // Caps each color at its stock in the selected size, then trims the pack to q units.
    trimTo(q) {
      this.picks.forEach((qty, productId) => this.setPick(productId, Math.min(qty, this.color(productId)?.stock || 0)));
      for (const productId of [...this.picks.keys()].reverse()) {
        if (this.total() <= q) break;
        this.setPick(productId, this.picks.get(productId) - (this.total() - q));
      }
    }

    total() {
      let sum = 0;
      this.picks.forEach((qty) => (sum += qty));
      return sum;
    }

    toggleBuilder() {
      this.customizing = !this.customizing;
      // Closing the builder goes back to a pack of the color picked above.
      if (!this.customizing) {
        this.seed();
        this.previewGallery(this.productId);
      }
      this.hideError();
      this.render();
    }

    tapSwatch(productId) {
      if (!this.color(productId)) return;
      this.previewGallery(productId);
      if (this.picks.has(String(productId))) return;
      if (this.total() >= this.q) {
        // Keep the pack full: the new color takes a unit from the color with the most.
        let donor = null;
        this.picks.forEach((qty, id) => {
          if (!donor || qty > this.picks.get(donor)) donor = id;
        });
        this.setPick(donor, this.picks.get(donor) - 1);
      }
      this.setPick(productId, 1);
      this.render();
    }

    step(productId, delta) {
      const color = this.color(productId);
      if (!color) return;
      const next = (this.picks.get(String(productId)) || 0) + delta;
      if (delta > 0 && (this.total() >= this.q || next > color.stock)) return;
      this.setPick(productId, next);
      this.render();
    }

    totals() {
      let msrp = this.q * this.price;
      if (this.total() === this.q) {
        msrp = 0;
        this.picks.forEach((qty, id) => (msrp += qty * (this.color(id)?.price || this.price)));
      }
      return { msrp, now: msrp - this.q * unitOffAt(this.offer, this.q) };
    }

    /* ---------- render ---------- */

    renderTiles() {
      const noun = this.dataset.noun;
      this.grid.replaceChildren(
        ...[1, ...this.sizes].map((q) => {
          const save = q * unitOffAt(this.offer, q);
          const tier = document.createElement('div');
          tier.className = 'pack-selector__tier';
          tier.dataset.q = q;
          tier.setAttribute('role', 'radio');
          tier.setAttribute('aria-label', q === 1 ? `Single ${noun}` : `${q}-Pack, ${q} ${noun}s, save ${money(save)}`);
          const name = document.createElement('span');
          name.className = 'pack-selector__name';
          name.textContent = q === 1 ? 'Single' : `${q}-Pack`;
          tier.append(name);
          if (q > 1) {
            const saving = document.createElement('span');
            saving.className = 'pack-selector__save';
            saving.textContent = `Save ${money(save)}`;
            tier.append(saving);
          }
          return tier;
        }),
      );
    }

    renderSwatches() {
      const colors = [...this.colors.values()].filter((c) => this.eligible(c));
      this.swatchList.replaceChildren(
        ...colors.map((c) => {
          const container = document.createElement('div');
          container.className = 'bundle-swatch-container';
          container.dataset.productId = c.productId;

          const swatch = document.createElement('div');
          swatch.className = 'bundle-swatch';
          swatch.setAttribute('role', 'button');
          swatch.setAttribute('tabindex', '0');
          swatch.setAttribute('aria-label', c.label);
          const inner = document.createElement('div');
          inner.className = 'bundle-swatch-inner';
          inner.style.background = String(c.background || '').replace(/;\s*$/, '');
          swatch.append(inner);

          const stepper = document.createElement('div');
          stepper.className = 'bundle-number-input';
          const dec = document.createElement('button');
          dec.type = 'button';
          dec.className = 'bundle-number-input-decrement';
          dec.textContent = '-';
          dec.setAttribute('aria-label', `One fewer ${c.label}`);
          const input = document.createElement('input');
          input.type = 'number';
          input.min = '0';
          input.max = String(c.stock);
          input.value = '0';
          input.setAttribute('aria-label', `${c.label} quantity`);
          const inc = document.createElement('button');
          inc.type = 'button';
          inc.className = 'bundle-number-input-increment';
          inc.textContent = '+';
          inc.setAttribute('aria-label', `One more ${c.label}`);
          stepper.append(dec, input, inc);

          container.append(swatch, stepper);
          return container;
        }),
      );
    }

    render() {
      const multi = this.q > 1;
      const total = this.total();
      const totals = this.totals();
      this.toggleAttribute('data-multi', multi);

      this.link.hidden = !multi;
      this.link.setAttribute('aria-expanded', multi && this.customizing);
      this.builder.hidden = !(multi && this.customizing);

      this.swatchList.querySelectorAll('.bundle-swatch-container').forEach((container) => {
        const productId = container.dataset.productId;
        const qty = this.picks.get(productId) || 0;
        const swatch = container.querySelector('.bundle-swatch');
        swatch.classList.toggle('selected', qty > 0);
        swatch.setAttribute('aria-pressed', qty > 0);
        container.querySelector('input').value = qty;
        container.querySelector('.bundle-number-input-increment').disabled =
          total >= this.q || qty >= (this.color(productId)?.stock || 0);
      });

      const label = this.querySelector('.selected-colors');
      label.textContent = [...this.picks.keys()].map((id) => this.colors.get(id)?.label).join(', ');

      this.renderCta(total, totals);
      this.renderPrice(totals);
    }

    renderCta(total, { msrp, now }) {
      if (!this.cta || this.busy || this.q === 1) return;
      const missing = this.q - total;
      if (missing > 0) {
        this.cta.disabled = true;
        this.cta.innerHTML = `<span class="btn-text">Choose ${missing} more</span>`;
        return;
      }
      this.cta.disabled = false;
      this.cta.innerHTML =
        `<span class="btn-text">Add to Bag — ` + (msrp > now ? `<s>${money(msrp)}</s> ` : '') + `${money(now)}</span>`;
    }

    renderPrice({ msrp, now }) {
      ['price', 'price-desktop'].forEach((prefix) => {
        const container = document.getElementById(`${prefix}-${this.sectionId}`);
        if (!container) return;
        container.querySelector('.pack-price')?.remove();
        container.classList.toggle('has-pack-price', this.q > 1);
        if (this.q === 1) return;
        container.insertAdjacentHTML(
          'afterbegin',
          `<div class="price price--large price--on-sale pack-price">
            <div class="price__container"><div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">${this.q}-Pack price</span>
              <span class="price-item price-item--sale price-item--last">${money(now)}</span>
              ${
                msrp > now
                  ? `<span class="visually-hidden visually-hidden--inline">Regular price</span>
                     <span><s class="price-item price-item--regular">${money(msrp)}</s></span>`
                  : ''
              }
            </div></div>
          </div>`,
        );
      });
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    // Shows the tapped color's photos, as the color swatches above do, without leaving this product.
    previewGallery(productId) {
      const id = String(productId);
      const color = this.colors.get(id);
      if (!color || this.galleryProductId === id) return;
      this.galleryProductId = id;
      // One section fetch per color; each holds that color's gallery in the selected size.
      if (!this.gallerySections.has(id)) {
        this.gallerySections.set(
          id,
          fetch(`${color.url}?variant=${color.variantId}&section_id=${this.sectionId}`)
            .then((response) => response.text())
            .then((html) => new DOMParser().parseFromString(html, 'text/html')),
        );
      }
      this.gallerySections
        .get(id)
        .then((doc) => {
          if (this.galleryProductId !== id) return;
          this.stopAnchor?.();
          this.stopAnchor = holdViewportAnchor(this, 1600);
          swapGallery(this.productInfo, doc);
        })
        .catch((e) => {
          this.gallerySections.delete(id);
          console.error('pack-selector: gallery preview failed', e);
        });
    }

    /* ---------- events ---------- */

    onClick(e) {
      if (this.state !== 'ready') return;
      const tier = e.target.closest('.pack-selector__tier');
      if (tier) return this.select(Number(tier.dataset.q));
      if (e.target.closest('.pack-selector__customize')) return this.toggleBuilder();

      const container = e.target.closest('.bundle-swatch-container');
      if (!container) return;
      const productId = container.dataset.productId;
      if (e.target.closest('.bundle-number-input-increment')) {
        e.preventDefault();
        this.step(productId, 1);
      } else if (e.target.closest('.bundle-number-input-decrement')) {
        e.preventDefault();
        this.step(productId, -1);
      } else if (e.target.closest('.bundle-swatch')) {
        this.tapSwatch(productId);
      }
    }

    onKeydown(e) {
      if (this.state !== 'ready') return;
      const swatch = e.target.closest('.bundle-swatch');
      if (swatch && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        return this.tapSwatch(swatch.closest('.bundle-swatch-container').dataset.productId);
      }

      const tier = e.target.closest('.pack-selector__tier');
      if (!tier) return;
      const q = Number(tier.dataset.q);
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        return this.select(q);
      }
      const delta =
        e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
      if (!delta) return;
      e.preventDefault();
      const order = [1, ...this.sizes];
      const next = order[(order.indexOf(q) + delta + order.length) % order.length];
      this.select(next, true);
    }

    onInputChange(e) {
      const input = e.target.closest('.bundle-swatch-container input');
      if (!input || this.state !== 'ready') return;
      const productId = input.closest('.bundle-swatch-container').dataset.productId;
      const others = this.total() - (this.picks.get(productId) || 0);
      const max = Math.min(this.color(productId)?.stock || 0, this.q - others);
      this.setPick(productId, Math.max(0, Math.min(parseInt(input.value, 10) || 0, max)));
      this.render();
    }

    onVariantChange({ data }) {
      if (data.sectionId !== this.sectionId || !data.variant) return;
      this.variantId = String(data.variant.id);
      this.price = data.variant.price;
      this.readColors();
      if (this.state === 'pending') return;
      // resolve() re-selects the current pack; a customized mix is kept, capped at the new size's stock.
      whenIgReady((ig) => this.resolve(ig));
    }

    /* ---------- offer + cart ---------- */

    assignOffer() {
      const ig = window.igData;
      if (!this.offer) return;
      if (!ig?.campaigns) return whenIgReady((ready) => ready && this.assignOffer());
      if (ig.campaigns.getAll().some((c) => c.id === this.offer.id)) return;
      try {
        ig.user.assignTestGroup(this.offer.id, this.offer.variationId);
        ig.reset();
      } catch (e) {
        console.error('pack-selector: offer assignment failed', e);
      }
    }

    async onAdd(e) {
      e.preventDefault();
      if (this.q === 1 || this.total() !== this.q || this.busy) return;
      this.assignOffer();
      this.hideError();

      const group = 'hwb-' + crypto.randomUUID();
      const items = [...this.picks].map(([productId, quantity]) => ({
        id: this.color(productId).variantId,
        quantity,
        properties: { _bundle_tier: String(this.q), _bundle_group: group },
      }));
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { items };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      this.busy = true;
      this.cta.setAttribute('aria-disabled', 'true');
      this.cta.classList.add('loading');
      try {
        const response = await fetch(`${routes.cart_add_url}.js`, { ...fetchConfig(), body: JSON.stringify(body) });
        const data = await response.json();
        if (data.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'pack-selector',
            productVariantId: items[0].id,
            errors: data.errors || data.description,
            message: data.message,
          });
          this.showError(data.description || data.message);
          return;
        }
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pack-selector', productVariantId: items[0].id, cartData: data });
        cart.classList.remove('is-empty');
        cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
        cart.renderContents(data);
      } catch (error) {
        console.error(error);
        this.showError(window.cartStrings?.error || 'Something went wrong. Please try again.');
      } finally {
        this.busy = false;
        this.cta.classList.remove('loading');
        this.cta.removeAttribute('aria-disabled');
        this.renderCta(this.total(), this.totals());
      }
    }
  }

  customElements.define('pack-selector', PackSelector);
}
